import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    Divider,
    Chip,
    IconButton,
    CircularProgress,
    alpha,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    FormatListBulleted as ListIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import type { BaseballRecruitProfile } from 'api/BaseballRecruits';

export interface PreviousScrapesSectionProps {
    profiles: BaseballRecruitProfile[];
    scrapingProfiles: Set<string>;
    getPlayerDisplayName: (profile: BaseballRecruitProfile) => string;
    getStatusChip: (profile: BaseballRecruitProfile) => React.ReactNode;
    onScrape: (uuid: string) => void;
    onDeleteProfile: (uuid: string) => void;
    onUnlinkProfile: (uuid: string) => void;
}

export function PreviousScrapesSection({
    profiles,
    scrapingProfiles,
    getPlayerDisplayName,
    getStatusChip,
    onScrape,
    onDeleteProfile,
    onUnlinkProfile,
}: PreviousScrapesSectionProps) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: alpha(theme.palette.text.primary, 0.12),
                borderRadius: 2,
                p: 1.5,
                background: alpha(theme.palette.text.primary, 0.01),
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    letterSpacing: 1,
                    fontSize: '0.85rem',
                }}
            >
                Previous Scrapes ({profiles.length})
            </Typography>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ListIcon fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={700}>
                            Player Profiles
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {profiles.length === 0 ? (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 2,
                                color: 'text.secondary',
                            }}
                        >
                            <Typography variant="body2">
                                No player profiles yet. Use the tools above to import players.
                            </Typography>
                        </Box>
                    ) : (
                        <List disablePadding>
                            {profiles.map((profile, index) => (
                                <React.Fragment key={profile.uuid}>
                                    {index > 0 && <Divider />}
                                    <ListItem
                                        sx={{
                                            display: 'flex',
                                            flexDirection: {
                                                xs: 'column',
                                                sm: 'row',
                                            },
                                            alignItems: {
                                                xs: 'stretch',
                                                sm: 'center',
                                            },
                                            gap: 1,
                                            py: 1.5,
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {getPlayerDisplayName(profile)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {profile.maxpreps_url}
                                            </Typography>
                                            {profile.player_bio && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {profile.player_bio.school}
                                                    {profile.player_bio.class_year &&
                                                        ` · Class of ${profile.player_bio.class_year}`}
                                                    {profile.player_bio.positions?.length > 0 &&
                                                        ` · ${profile.player_bio.positions.join(
                                                            ', ',
                                                        )}`}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {getStatusChip(profile)}
                                            {profile.individual_uuid && (
                                                <Chip
                                                    icon={<LinkOffIcon />}
                                                    label="Unlink"
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    onClick={() => onUnlinkProfile(profile.uuid)}
                                                    title="Unlink from individual — allows re-matching"
                                                />
                                            )}
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => onScrape(profile.uuid)}
                                                disabled={scrapingProfiles.has(profile.uuid)}
                                                title="Scrape now"
                                            >
                                                {scrapingProfiles.has(profile.uuid) ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    <RefreshIcon />
                                                )}
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => onDeleteProfile(profile.uuid)}
                                                title="Remove"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
